import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.css';
import LoginHistory from '../../components/LoginHistory/LoginHistory';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    jobTitle: '',
    phone: '',
    profileImage: null
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (!userData) {
      navigate('/login');
      return;
    }
    setUser(userData);
    setFormData({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      companyName: userData.companyName || '',
      jobTitle: userData.jobTitle || '',
      phone: userData.phone || '',
      profileImage: userData.profileImage || null
    });
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          profileImage: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // TODO: Implement API call to update profile
    setIsEditing(false);
    // For now, just update local storage
    localStorage.setItem('user', JSON.stringify({ ...user, ...formData }));
    setUser(prev => ({ ...prev, ...formData }));
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  const handleLoginHistoryClick = () => {
    setShowLoginHistory(true);
  };

  if (!user) return null;

  return (
    <div className={styles.profileWrapper}>
      <div className={styles.profileHeader}>
        <div className={styles.headerContent}>
          <h1>My Profile</h1>
          <p>Manage your account settings and preferences</p>
        </div>
      </div>

      <div className={styles.profileContainer}>
        <div className={styles.sidebar}>
          <div className={styles.profileImage}>
            {formData.profileImage ? (
              <img src={formData.profileImage} alt="Profile" />
            ) : (
              <div className={styles.initials}>
                {formData.firstName?.[0]}{formData.lastName?.[0]}
              </div>
            )}
            {isEditing && (
              <label className={styles.uploadButton}>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  hidden 
                />
                <i className="fas fa-camera"></i>
              </label>
            )}
          </div>
          <div className={styles.profileStats}>
            <div className={styles.statItem}>
              <i className="fas fa-building"></i>
              <div className={styles.statInfo}>
                <h3>Construction Projects</h3>
                <span>8 Active</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-file-contract"></i>
              <div className={styles.statInfo}>
                <h3>Bids Managed</h3>
                <span>24 Total</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-boxes"></i>
              <div className={styles.statInfo}>
                <h3>Inventory Items</h3>
                <span>156 Tracked</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-hard-hat"></i>
              <div className={styles.statInfo}>
                <h3>Team Members</h3>
                <span>12 Active</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-chart-line"></i>
              <div className={styles.statInfo}>
                <h3>Project Efficiency</h3>
                <span>92% Improved</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-clock"></i>
              <div className={styles.statInfo}>
                <h3>Time Saved</h3>
                <span>120 Hours</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.actionHeader}>
            <h2>Personal Information</h2>
            <button 
              className={styles.editButton}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? (
                <>
                  <i className="fas fa-times"></i>
                  Cancel
                </>
              ) : (
                <>
                  <i className="fas fa-edit"></i>
                  Edit Profile
                </>
              )}
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.profileForm}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isEditing && (
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton}>
                  <i className="fas fa-save"></i>
                  Save Changes
                </button>
              </div>
            )}
          </form>

          <div className={styles.securitySection}>
            <h2>Security</h2>
            <div className={styles.securityOptions}>
              <button className={styles.securityButton} onClick={handleChangePassword}>
                <i className="fas fa-key"></i>
                Change Password
              </button>
              <button className={styles.securityButton}>
                <i className="fas fa-shield-alt"></i>
                Two-Factor Authentication
              </button>
              <button className={styles.securityButton} onClick={handleLoginHistoryClick}>
                <i className="fas fa-history"></i>
                Login History
              </button>
            </div>
          </div>

          <LoginHistory 
            isOpen={showLoginHistory} 
            onClose={() => setShowLoginHistory(false)} 
          />
        </div>
      </div>
    </div>
  );
};

export default Profile; 